import { FeeType, TaxType } from '@wix/events-types';
import { WIX_FEE_RATE } from '../constants/event';
import { getValidGiftCardDetails } from '../gift-cards/gift-cards';
import { getFormattedMoney } from '../price/formatter';
import { getMoney, roundPrice } from '../price/utils';
import { getBuyerTotal } from './order';
import { getAddedFee, getFee, getIncludedFee, getPreliminaryTax, hasAddedFee, hasIncludedFee, hasTax, isFeeAdded, isTaxAdded, } from './tax';
import { getTicketDefinitionCurrency, isDonationTicketDefinition } from './ticket-definition';
export const getPaidPlanDiscount = (invoice) => invoice?.discount?.discounts?.find(item => item.paidPlan);
export const getCouponDiscount = (invoice) => invoice?.discount?.discounts?.find(item => item.coupon);
export const getCouponDiscountCode = (invoice) => getCouponDiscount(invoice)?.coupon?.code;
export const getCouponDiscountName = (invoice) => getCouponDiscount(invoice)?.coupon?.name;
export const getCouponDiscountId = (invoice) => getCouponDiscount(invoice)?.coupon?.couponId;
export const getCouponDiscountAmount = (invoice) => getCouponDiscount(invoice)?.amount;
export const hasPaidPlanDiscount = (invoice) => Boolean(getPaidPlanDiscount(invoice));
export const hasCouponDiscount = (invoice) => Boolean(getCouponDiscount(invoice));
export const hasDiscount = (invoice) => Boolean(Number(invoice?.discount?.amount?.amount));
export const hasSubtotal = (invoice, giftCardPaymentDetails) => hasDiscount(invoice) ||
    hasTax(invoice) ||
    hasAddedFee(invoice) ||
    !!getValidGiftCardDetails(giftCardPaymentDetails)?.length;
export const getPercentOffRate = (paidPlan) => paidPlan.discount?.percentOffRate ?? '0';
export const getCheckoutSummary = (invoice, moneyFormatter = getFormattedMoney, giftCardPaymentDetails) => {
    const buyerTotal = getBuyerTotal(invoice.grandTotal, giftCardPaymentDetails);
    const validGiftCardDetails = getValidGiftCardDetails(giftCardPaymentDetails);
    return {
        subtotal: hasSubtotal(invoice, giftCardPaymentDetails) ? moneyFormatter(invoice.subTotal) : null,
        paidPlanDiscount: hasPaidPlanDiscount(invoice) ? `-${moneyFormatter(getPaidPlanDiscount(invoice).amount)}` : null,
        discount: hasCouponDiscount(invoice) ? `-${moneyFormatter(getCouponDiscount(invoice).amount)}` : null,
        tax: hasTax(invoice) ? moneyFormatter(invoice.tax.amount) : null,
        addedFee: hasAddedFee(invoice) ? moneyFormatter(getAddedFee(invoice).amount) : null,
        includedFee: hasIncludedFee(invoice) ? moneyFormatter(getIncludedFee(invoice).amount) : null,
        total: moneyFormatter(invoice.grandTotal),
        ticketTotal: moneyFormatter(invoice.subTotal),
        buyerTotal: moneyFormatter(buyerTotal),
        revenue: moneyFormatter(invoice.revenue ?? {}),
        giftCards: validGiftCardDetails?.length
            ? validGiftCardDetails.map(({ obfuscatedCode, amount }) => ({ obfuscatedCode, amount: moneyFormatter(amount) }))
            : undefined,
    };
};
export const getCurrencyFromTickets = (ticketDefinitions) => ticketDefinitions.length ? getTicketDefinitionCurrency(ticketDefinitions[0]) : '';
export const getPreliminaryInvoice = (ticketDefinitions, selectedItems, taxConfig, paidPlan) => {
    const currency = getCurrencyFromTickets(ticketDefinitions);
    let items = getPreliminaryItems(ticketDefinitions, selectedItems, currency);
    const subTotalAmount = calculateSubTotal(items);
    const subTotal = getMoney(subTotalAmount, currency);
    let grandTotalAmount = Number(subTotal.amount);
    const { discount, items: newItems, grandTotalAmount: updatedGrandTotalAmount, } = calculatePaidPlanDiscount(paidPlan, items, grandTotalAmount);
    items = newItems;
    grandTotalAmount = updatedGrandTotalAmount;
    items = appendTaxAndFeesToItems(items, ticketDefinitions, taxConfig, currency);
    const taxAmount = calculateTax(items);
    const taxableAmount = calculateTaxable(items);
    const addedFeeAmount = calculateAddedFee(items);
    const includedFeeAmount = calculateIncludedFee(items);
    grandTotalAmount += addedFeeAmount;
    if (isTaxAdded(taxConfig?.type)) {
        grandTotalAmount = grandTotalAmount + taxAmount;
    }
    else if (taxConfig?.type === TaxType.INCLUDED) {
        subTotal.value = subTotal.amount = roundPrice(Number(subTotal.amount) - taxAmount, currency);
    }
    const fees = [];
    if (addedFeeAmount) {
        // TODO: What to do here? (probably will need to change to FeeType.FEE_ADDED_AT_CHECKOUT after dashboard is changed to create with this FeeType)
        fees.push(getFee(FeeType.FEE_ADDED, getMoney(addedFeeAmount, currency)));
    }
    if (includedFeeAmount) {
        fees.push(getFee(FeeType.FEE_INCLUDED, getMoney(includedFeeAmount, currency)));
    }
    const grandTotal = getMoney(grandTotalAmount, currency);
    return {
        items: convertExtendedItemsToItems(items),
        discount,
        subTotal,
        grandTotal,
        tax: taxConfig
            ? {
                ...taxConfig,
                amount: getMoney(taxAmount, currency),
                taxable: getMoney(taxableAmount, currency),
            }
            : null,
        fees,
    };
};
const getEventCurrencyFromItems = (items) => items?.[0]?.price.currency ?? 'USD';
const orderItemsByPrice = (items) => [...items].sort((a, b) => {
    const aPrice = Number(a?.price?.amount ?? 0);
    const bPrice = Number(b?.price?.amount ?? 0);
    return bPrice - aPrice;
});
const getAmountPercent = (amount, percentOffRate, currencyCode) => roundPrice((Number(amount) / 100) * Number(percentOffRate), currencyCode);
const ensureDiscountExistence = (currentDiscount, defaultCurrency, currentTotalAmount) => currentDiscount || {
    discounts: [],
    amount: { amount: '0', currency: defaultCurrency },
    afterDiscount: { amount: currentTotalAmount, currency: defaultCurrency },
};
const addPaidPlanDiscount = (discount, name, amount, currency, quantityDiscounted, rate) => {
    const newDiscount = {
        ...discount,
        amount: { ...discount.amount },
        afterDiscount: { ...discount.afterDiscount },
        discounts: [...discount.discounts],
    };
    newDiscount.discounts.push({
        amount: { amount, currency },
        paidPlan: {
            name,
            percentDiscount: {
                quantityDiscounted,
                rate,
            },
        },
    });
    newDiscount.amount.amount = roundPrice(Number(newDiscount.amount.amount) + Number(amount), currency);
    newDiscount.afterDiscount.amount = roundPrice(Number(newDiscount.afterDiscount.amount) - Number(amount), currency);
    return newDiscount;
};
const calculatePaidPlanDiscount = (paidPlan, items, grandTotalAmount, discount) => {
    if (!paidPlan) {
        return {
            items,
            discount,
            grandTotalAmount,
        };
    }
    const { name, discount: { percentOffRate }, } = paidPlan;
    const currency = getEventCurrencyFromItems(items);
    const orderedItems = orderItemsByPrice(items);
    let quantitySum = 0;
    let discountAmountSum = 0;
    let { remainingCredits } = paidPlan;
    orderedItems.forEach(orderedItem => {
        if (remainingCredits && !orderedItem.isDonation) {
            const { total: { amount: totalAmount }, } = orderedItem;
            let totalDiscountForItem = 0;
            let totalTicketsDiscountedForItem = 0;
            orderedItem.ticketPrices = orderedItem.ticketPrices.map(ticketPrice => {
                if (!remainingCredits) {
                    return ticketPrice;
                }
                const ticketDiscountAmount = Number(getAmountPercent(ticketPrice, percentOffRate, currency));
                remainingCredits -= 1;
                totalTicketsDiscountedForItem += 1;
                totalDiscountForItem += ticketDiscountAmount;
                return ticketPrice - ticketDiscountAmount;
            });
            quantitySum += totalTicketsDiscountedForItem;
            discountAmountSum += totalDiscountForItem;
            orderedItem.discount = ensureDiscountExistence(orderedItem.discount, currency, totalAmount);
            orderedItem.discount = addPaidPlanDiscount(orderedItem.discount, name, roundPrice(totalDiscountForItem, currency), currency, totalTicketsDiscountedForItem, getPercentOffRate(paidPlan));
        }
    });
    let grandTotalAfterDiscount = grandTotalAmount;
    if (quantitySum) {
        discount = ensureDiscountExistence(discount, currency, roundPrice(grandTotalAmount, currency));
        discount = addPaidPlanDiscount(discount, name, roundPrice(discountAmountSum, currency), currency, quantitySum, getPercentOffRate(paidPlan));
        grandTotalAfterDiscount = grandTotalAfterDiscount - Number(discountAmountSum);
    }
    return {
        items,
        discount,
        grandTotalAmount: grandTotalAfterDiscount,
    };
};
const appendTaxAndFeesToItems = (items, ticketDefinitions, taxConfig, currency) => items.map(item => {
    const { wixFeeConfig } = ticketDefinitions.find(def => def.id === item.id);
    const taxesAndFees = item.ticketPrices.map(ticketPrice => {
        const ticketTax = getPreliminaryTax(taxConfig, ticketPrice, currency, item.isDonation);
        const subtotal = isTaxAdded(ticketTax?.type) ? ticketPrice + Number(ticketTax.amount.amount) : ticketPrice;
        const ticketFees = getPreliminaryFees(wixFeeConfig, subtotal, currency);
        return { tax: ticketTax, fees: ticketFees };
    });
    const { tax, fees } = taxesAndFees.reduce(({ tax: taxItem, fees: feesItem }, { tax: taxSum, fees: feesSum }) => {
        if (taxSum) {
            taxSum.taxable.value = taxSum.taxable.amount = roundPrice(Number(taxSum.taxable.amount) + Number(taxItem.taxable.amount), currency);
            taxSum.amount.value = taxSum.amount.amount = roundPrice(Number(taxSum.amount.amount) + Number(taxItem.amount.amount), currency);
        }
        if (feesSum.length) {
            feesSum[0].amount.value = feesSum[0].amount.amount = roundPrice(Number(feesSum[0].amount.amount) + Number(feesItem[0].amount.amount), currency);
        }
        return { tax: taxSum, fees: feesSum };
    });
    return {
        ...item,
        tax,
        fees,
    };
});
const convertExtendedItemsToItems = (extendedItems) => extendedItems.map(extendedItem => {
    const newItem = {};
    const bannedKeys = ['ticketPrices', 'isDonation'];
    Object.keys(extendedItem).forEach(key => {
        if (!bannedKeys.includes(key)) {
            ;
            newItem[key] = extendedItem[key];
        }
    });
    return newItem;
});
const getPreliminaryItems = (ticketDefinitions, selectedItems, currency) => {
    const picks = Object.entries(selectedItems).filter(([, { quantity }]) => Boolean(quantity));
    return picks
        .map(([ticketDefId, { quantity, priceOverrides }]) => {
        const ticketDefinition = ticketDefinitions.find(def => def.id === ticketDefId);
        const { id, name, price } = ticketDefinition;
        const isDonation = isDonationTicketDefinition(ticketDefinition);
        if (priceOverrides?.length) {
            const groupedPrices = Object.values(priceOverrides.reduce((groups, cur) => ({
                ...groups,
                [cur]: [...(groups[cur] || []), cur],
            }), {}));
            return groupedPrices.map(ticketPrices => ({
                id,
                name,
                price: getMoney(Number(ticketPrices[0]), currency),
                quantity: ticketPrices.length,
                total: getMoney(Number(ticketPrices[0]) * ticketPrices.length, currency),
                ticketPrices: ticketPrices.map(Number),
                isDonation,
            }));
        }
        else {
            const ticketPrice = Number(price.amount);
            return [
                {
                    id,
                    name,
                    price: getMoney(ticketPrice, currency),
                    quantity,
                    total: getMoney(Number(ticketPrice) * quantity, currency),
                    ticketPrices: Array(quantity).fill(ticketPrice),
                    isDonation,
                },
            ];
        }
    })
        .flat();
};
const getPreliminaryFees = (wixFeeConfig, total, currency) => wixFeeConfig ? [getFee(wixFeeConfig.type, getMoney(total * WIX_FEE_RATE, currency))] : [];
const calculateSubTotal = (items) => items.reduce((amount, item) => amount + Number(item.total.amount), 0);
const calculateTax = (items) => items.reduce((amount, item) => amount + Number(item.tax?.amount.amount ?? 0), 0);
const calculateTaxable = (items) => items.reduce((amount, item) => amount + Number(item.tax?.taxable.amount ?? 0), 0);
const calculateAddedFee = (items) => items.reduce((amount, item) => (isFeeAdded(item.fees[0]?.type) ? amount + Number(item.fees[0].amount.amount) : amount), 0);
const calculateIncludedFee = (items) => items.reduce((amount, item) => item.fees[0]?.type === FeeType.FEE_INCLUDED ? amount + Number(item.fees[0].amount.amount) : amount, 0);
export const getTaxLabel = (invoice) => hasTax(invoice) ? `${invoice.tax.name} (${invoice.tax.rate}%)` : null;
export const getMembershipLabel = (invoice) => hasPaidPlanDiscount(invoice) ? getPaidPlanDiscount(invoice).paidPlan.name : null;
export const getPaidPlanPercentDiscount = (invoice) => hasPaidPlanDiscount(invoice) ? Number(getPaidPlanDiscount(invoice).paidPlan.percentDiscount.rate).toString() : null;
